import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Badge, Card, CardHeader, EmptyState, Table, Tabs,
} from '@opendate-app/setlist';
import WindowPicker from '../../ReportingDashboard/components/attribution/WindowPicker';
import { MODEL_TABS, formatTrackingDate, toQueryString } from '../../ReportingDashboard/components/attribution/attributionReportUtils';

const axios = require('axios').default;

const mutedStyle = { color: 'var(--sl-text-tertiary)' };
const pathStyle = {
  display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom',
};

const ROLE_BADGES = {
  credited: { color: 'success', label: 'Credited' },
  influencer: { color: 'blue', label: 'Influencer' },
  outside_lookback: { color: 'gray', label: 'Outside lookback' },
  not_tracked: { color: 'warning', label: 'Before tracking started' },
};

const formatShare = (pct) => `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;

export function RoleBadge({ role, sharePct }) {
  const badge = ROLE_BADGES[role];
  if (!badge) return null;
  return <Badge color={badge.color} size="sm">{sharePct == null ? badge.label : `${badge.label} · ${formatShare(sharePct)}`}</Badge>;
}

const blank = (value) => value || <span style={mutedStyle}>—</span>;

const columns = (showRoles) => [
  {
    key: 'occurredAt', header: 'Time', primary: true, render: (row) => (row.synthetic ? <span style={mutedStyle}>At purchase</span> : row.occurredAt),
  },
  { key: 'channel', header: 'Channel', render: (row) => row.channel },
  { key: 'source', header: 'Source', render: (row) => blank(row.source) },
  { key: 'campaign', header: 'Campaign', render: (row) => blank(row.campaign) },
  {
    key: 'landingPath', header: 'Landing page', render: (row) => (row.landingPath ? <span style={pathStyle} title={row.landingPath}>{row.landingPath}</span> : blank(null)),
  },
  { key: 'surface', header: 'Surface', render: (row) => (row.synthetic ? <span style={mutedStyle}>No tracked visit</span> : blank(row.surface)) },
  { key: 'visitor', header: 'Visitor', render: (row) => blank(row.visitor && <code>{row.visitor}</code>) },
  ...(showRoles ? [{ key: 'role', header: 'Role', render: (row) => <RoleBadge role={row.role} sharePct={row.sharePct} /> }] : []),
];

function AttributionTimelineCard({ timeline }) {
  const isOrder = timeline.kind === 'order';
  const [view, setView] = useState({
    model: timeline.model,
    windowDays: timeline.windowDays,
    rows: timeline.rows,
    truncatedAt: timeline.truncatedAt,
    shown: { model: timeline.model, windowDays: timeline.windowDays },
    loading: false,
    error: timeline.error || null,
  });
  const latestRequest = useRef(0);

  const load = (model, windowDays) => {
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    setView((previous) => ({
      ...previous, model, windowDays, loading: true, error: null,
    }));
    axios.get(`${timeline.dataUrl}?${toQueryString({ model, window_days: windowDays })}`)
      .then(({ data }) => {
        if (latestRequest.current === request) {
          setView((previous) => ({
            ...previous, rows: data.rows, truncatedAt: data.truncatedAt, loading: false, shown: { model, windowDays },
          }));
        }
      })
      .catch((error) => {
        if (latestRequest.current !== request) return;
        setView((previous) => ({
          ...previous,
          ...previous.shown,
          loading: false,
          error: error?.response?.data?.error || 'Could not load the touch timeline.',
        }));
      });
  };

  const actions = isOrder && timeline.dataUrl && (
    <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
      <Tabs variant="button-border" size="sm" collapseBelow={0} aria-label="Attribution model" items={MODEL_TABS} value={view.model} onChange={(model) => load(model, view.windowDays)} />
      <WindowPicker windowDays={view.windowDays} defaultWindowDays={timeline.defaultWindowDays} maxWindowDays={timeline.maxWindowDays} onChange={(windowDays) => load(view.model, windowDays)} />
    </div>
  );

  return (
    <Card
      padding="none"
      header={(
        <CardHeader
          title="Touch timeline"
          supportingText={[
            timeline.trackingStartedAt ? `Tracking since ${formatTrackingDate(timeline.trackingStartedAt)}.` : 'Tracking hasn\'t started for this team yet.',
            view.truncatedAt && `Showing the most recent ${view.truncatedAt} touches.`,
          ].filter(Boolean).join(' ')}
          actions={actions || undefined}
        />
      )}
    >
      {view.error && <div className="p-3"><Alert color="error" title={view.error} /></div>}
      <Table
        columns={columns(isOrder)}
        data={view.rows}
        getRowKey={(row) => row.key}
        loading={view.loading}
        empty={<EmptyState size="sm" title="No tracked visits" />}
      />
    </Card>
  );
}

AttributionTimelineCard.propTypes = {
  timeline: PropTypes.shape({
    kind: PropTypes.oneOf(['order', 'fan']).isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired })).isRequired,
    model: PropTypes.string,
    windowDays: PropTypes.number,
    defaultWindowDays: PropTypes.number,
    maxWindowDays: PropTypes.number,
    trackingStartedAt: PropTypes.string,
    dataUrl: PropTypes.string,
    truncatedAt: PropTypes.number,
    error: PropTypes.string,
  }).isRequired,
};

export default AttributionTimelineCard;
